import React, { useMemo, useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import {
    CircularProgress,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
} from "@mui/material";

import ClipLoader from "react-spinners/ClipLoader";

import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import LazyScrollList from "components/organisms/LazyScrollList";

import {
    COLORS,
    LIST_BATCH_SIZE,
    LIST_INITIAL_COUNT,
} from "../../SignalWizard.constants";
import {
    filterBySearch,
    filterByType,
    getTypeOptions,
    isDeletable,
} from "../../SignalWizard.helpers";
import {
    Card,
    CountPill,
    SectionHeader,
    SectionTitle,
    StatusDot,
} from "../../SignalWizard.styled";
import {
    Controls,
    EmptyState,
    EmptyTitle,
    FilterButton,
    FilterOptionCount,
    FilterOptionIcon,
    IconTile,
    ItemInfo,
    ItemMeta,
    ItemName,
    ItemTitleRow,
    ListBody,
    LoaderWrap,
    Row,
    RowActions,
    RowCheckbox,
    RowMain,
    SearchInput,
    SearchWrap,
} from "./SelectableList.styled";

const stopPropagation = (event) => event.stopPropagation();

/**
 * Searchable, checkable list of sources or destinations with a per-row
 * overflow menu for deleting items that aren't used by any tracker.
 */
const SelectableList = ({
    language,
    items,
    typeKey,
    getIcon,
    isSelected,
    onToggle,
    isItemDisabled = () => false,
    disabledTooltip,
    onDelete,
    inUseTooltip,
    sectionTitle,
    searchPlaceholder,
    emptyTitle,
    emptyDescription,
    emptyAction,
    isLoading = false,
    itemTestId,
}) => {
    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
    const [menu, setMenu] = useState({ anchorEl: null, item: null });
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const typeOptions = useMemo(
        () => getTypeOptions(items, typeKey),
        [items, typeKey],
    );
    const activeTypeOption = typeOptions.find(
        (option) => option.code === typeFilter,
    );
    const activeTypeCode = activeTypeOption?.code ?? "";

    const visibleItems = useMemo(
        () =>
            filterBySearch(
                filterByType(items, activeTypeCode, typeKey),
                query,
                typeKey,
            ),
        [items, activeTypeCode, query, typeKey],
    );

    const getTypeIcon = (code) =>
        getIcon(items.find((item) => item?.[typeKey]?.code === code));

    const selectType = (code) => {
        setTypeFilter(code);
        setTypeMenuAnchor(null);
    };
    const selectedCount = items.filter(isSelected).length;

    const closeMenu = () => setMenu({ anchorEl: null, item: null });

    const confirmDelete = async () => {
        const item = pendingDelete;
        setPendingDelete(null);
        if (!item) return;
        setDeletingId(item.id);
        try {
            await onDelete(item);
        } finally {
            setDeletingId(null);
        }
    };

    const renderRow = (item) => {
        const isDisabled = isItemDisabled(item);
        const checked = isSelected(item);
        const toggle = () => !isDisabled && onToggle(item, !checked);

        const row = (
            <Row key={item.id} $disabled={isDisabled} onClick={toggle}>
                <RowMain>
                    <RowCheckbox
                        checked={checked}
                        disabled={isDisabled}
                        disableRipple
                        onClick={stopPropagation}
                        onChange={toggle}
                        inputProps={{ "data-cy": itemTestId }}
                    />
                    <IconTile>
                        {getIcon(item) && <img src={getIcon(item)} alt="" />}
                    </IconTile>
                    <ItemInfo>
                        <ItemTitleRow>
                            <ItemName>{item.name}</ItemName>
                        </ItemTitleRow>
                        <ItemMeta>{item?.[typeKey]?.name}</ItemMeta>
                    </ItemInfo>
                </RowMain>
                {onDelete && (
                    <RowActions onClick={stopPropagation}>
                        {deletingId === item.id ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <IconButton
                                size="small"
                                title={
                                    language?.tracker_wizard?.options ??
                                    "Options"
                                }
                                disabled={deletingId !== null}
                                onClick={(event) =>
                                    setMenu({
                                        anchorEl: event.currentTarget,
                                        item,
                                    })
                                }
                            >
                                <MoreHorizIcon fontSize="small" />
                            </IconButton>
                        )}
                    </RowActions>
                )}
            </Row>
        );

        return isDisabled && disabledTooltip ? (
            <Tooltip key={item.id} title={disabledTooltip} placement="top">
                {row}
            </Tooltip>
        ) : (
            row
        );
    };

    const renderBody = () => {
        if (isLoading) {
            return (
                <LoaderWrap>
                    <ClipLoader color={COLORS.primary} size={28} />
                </LoaderWrap>
            );
        }
        if (!items.length) {
            return (
                <EmptyState>
                    <EmptyTitle>{emptyTitle}</EmptyTitle>
                    <span>{emptyDescription}</span>
                    {emptyAction}
                </EmptyState>
            );
        }
        if (!visibleItems.length) {
            return (
                <EmptyState>
                    <span>
                        {language?.tracker_wizard?.no_search_results ??
                            "Nothing matches your filters."}
                    </span>
                </EmptyState>
            );
        }
        return (
            <ListBody>
                <LazyScrollList
                    items={visibleItems}
                    getItemKey={(item) => item.id}
                    initialCount={LIST_INITIAL_COUNT}
                    batchSize={LIST_BATCH_SIZE}
                    renderItem={renderRow}
                />
            </ListBody>
        );
    };

    const menuItem = menu.item;
    const canDeleteMenuItem = menuItem && isDeletable(menuItem);

    return (
        <>
            {items.length > 0 && (
                <Controls>
                    <SearchWrap>
                        <SearchInput
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={searchPlaceholder}
                            startAdornment={<SearchIcon />}
                            inputProps={{ "aria-label": searchPlaceholder }}
                        />
                    </SearchWrap>
                    <FilterButton
                        onClick={(event) =>
                            setTypeMenuAnchor(event.currentTarget)
                        }
                        aria-haspopup="listbox"
                        data-cy={itemTestId && `${itemTestId}-type-filter`}
                    >
                        <span className="filter-label">
                            {language?.tracker_wizard?.type ?? "Type"}:
                        </span>
                        <span>
                            {activeTypeOption?.name ??
                                language?.tracker_wizard?.all_types ??
                                "All Types"}
                        </span>
                        <ExpandMoreIcon />
                    </FilterButton>
                </Controls>
            )}

            <Menu
                anchorEl={typeMenuAnchor}
                open={Boolean(typeMenuAnchor)}
                onClose={() => setTypeMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
            >
                <MenuItem
                    selected={!activeTypeCode}
                    onClick={() => selectType("")}
                    sx={{ fontSize: 13 }}
                >
                    {language?.tracker_wizard?.all_types ?? "All Types"}
                    <FilterOptionCount>{items.length}</FilterOptionCount>
                </MenuItem>
                {typeOptions.map((option) => (
                    <MenuItem
                        key={option.code}
                        selected={option.code === activeTypeCode}
                        onClick={() => selectType(option.code)}
                        sx={{ fontSize: 13 }}
                    >
                        {getTypeIcon(option.code) ? (
                            <FilterOptionIcon
                                src={getTypeIcon(option.code)}
                                alt=""
                            />
                        ) : null}
                        {option.name}
                        <FilterOptionCount>{option.count}</FilterOptionCount>
                    </MenuItem>
                ))}
            </Menu>

            <SectionHeader>
                <SectionTitle>
                    <span>{sectionTitle}</span>
                    <StatusDot
                        $color={
                            selectedCount
                                ? COLORS.secondary
                                : COLORS.outlineVariant
                        }
                    />
                </SectionTitle>
                <CountPill>
                    {selectedCount}{" "}
                    {language?.tracker_wizard?.selected ?? "selected"}
                </CountPill>
            </SectionHeader>

            <Card>{renderBody()}</Card>

            <Menu
                anchorEl={menu.anchorEl}
                open={Boolean(menu.anchorEl)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Tooltip
                    title={canDeleteMenuItem ? "" : inUseTooltip}
                    placement="left"
                >
                    <span>
                        <MenuItem
                            disabled={!canDeleteMenuItem}
                            sx={{ color: COLORS.error, fontSize: 13 }}
                            onClick={() => {
                                setPendingDelete(menuItem);
                                closeMenu();
                            }}
                        >
                            <ListItemIcon sx={{ color: "inherit" }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </ListItemIcon>
                            {language?.buttons?.delete ?? "Delete"}
                        </MenuItem>
                    </span>
                </Tooltip>
            </Menu>

            <ConfirmationModal
                open={Boolean(pendingDelete)}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />
        </>
    );
};

export default SelectableList;
